/* eslint-disable multiline-ternary */
import * as React from 'react';
import PropTypes from 'prop-types';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Box,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import useDebounce from '../../../utils/useDebounce';
import { getLocations } from '../../../redux/location/actions';
import LocationChip from '../../LocationChip';
import { getLocationTypeDisplayName } from '../../../utils/enumHelper';

const LocationAutocomplete = ({
  onSelect,
  value,
  validation,
  multiple,
  label,
  limitTags,
  size,
  disabled,
  blurOnSelect,
  includeLocationBoards = false,
  helperText = null,
  icon,
}) => {
  const dispatch = useDispatch();

  // React state
  const [search, setSearch] = React.useState('');

  // Redux state
  const isFetchingLocations = useSelector((state) => state.location.isFetchingLocations);
  const locations = useSelector((state) => state.location.locations);
  const locationBoards = useSelector((state) => state.locationBoard.locationBoards);
  const locationsMap = useSelector((state) => state.location.locationsMap);
  const permissions = useSelector((state) => state.user.permissions);

  const debouncedSearch = useDebounce(search, 500);

  React.useEffect(() => {
    if (debouncedSearch.length > 0 || locations.content.length === 0) {
      dispatch(getLocations(0, 30, '&sort=name,asc', debouncedSearch));
    }
  }, [debouncedSearch]);

  const filteredLocationBoards = includeLocationBoards
    ? locationBoards.filter((lb) => locations.content.find((l) => l.id === lb.location.id)).map((lb) => ({
      ...lb.location,
      id: lb.id,
      locationBoard: lb,
    }))
    : []

  return (
    <Autocomplete
      id="location-select-autocomplete"
      limitTags={limitTags}
      blurOnSelect={blurOnSelect || !multiple}
      multiple={multiple}
      size={size}
      onChange={onSelect}
      sx={{ width: '100%' }}
      disabled={disabled}
      loading={isFetchingLocations}
      filterOptions={(x) => x}
      options={[...locations.content, ...filteredLocationBoards].sort((a, b) => -b.name.localeCompare(a.name))
        .sort((a, b) => -b.type.localeCompare(a.type))}
      value={value}
      noOptionsText={(permissions.admin || permissions.owner)
        ? (
          <ListItemButton
            onClick={() => { window.location.href = '/settings/locations' }}
            dense
          >
            <ListItemIcon><LocationOn /></ListItemIcon>
            <ListItemText
              primary="No locations found"
              secondary="Create one from the location settings page"
            />
          </ListItemButton>
        )
        : 'No locations found'}
      onBlur={() => { setSearch(''); }}
      isOptionEqualToValue={(option, valueCompare) => option.id === valueCompare.id}
      groupBy={(option) => getLocationTypeDisplayName(option)}
      getOptionLabel={(option) => (option.locationBoard && ` - ${option.locationBoard.name}`) || (option && option.name) || ''}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        option.locationBoard
          ? (
            <Box
              sx={{
                marginLeft: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography>-</Typography>
              <LocationChip
                key={`location-autocomplete-chip-${option.id}`}
                {...getTagProps({ index })}
                location={{
                  type: option.type,
                  name: 'asdfsdafs',
                }}
              />
            </Box>
          ) : (
            <LocationChip
              key={`location - autocomplete - chip - ${option.id} `}
              {...getTagProps({ index })}
              location={locationsMap[option.id]
                ? locationsMap[option.id][0]
                : {
                  name: 'Location Not Found',
                  type: 'Unknown',
                  // eslint-disable-next-line react/jsx-indent
                }}
            />
          )))}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          onChange={(e) => { setSearch(e.target.value); }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isFetchingLocations
                  ? <CircularProgress color="inherit" size={20} />
                  : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            startAdornment: icon && (
              <InputAdornment position="start">
                <LocationOn />
              </InputAdornment>
            ),
          }}
          error={!!validation}
          helperText={(validation && validation) || helperText}
        />
      )}
    />
  );
};

LocationAutocomplete.propTypes = {
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.object,
  validation: PropTypes.string,
  multiple: PropTypes.bool,
  size: PropTypes.string,
  limitTags: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  blurOnSelect: PropTypes.bool,
  includeLocationBoards: PropTypes.bool,
  helperText: PropTypes.string,
  icon: PropTypes.bool,
};

LocationAutocomplete.defaultProps = {
  value: null,
  validation: null,
  multiple: true,
  size: 'normal',
  limitTags: 5,
  label: 'Location',
  disabled: false,
  blurOnSelect: false,
  includeLocationBoards: false,
  helperText: null,
  icon: false,
};

export default LocationAutocomplete;
